import clsx from "clsx";
import { History, KeyRound, Lock, LogOut, MonitorSmartphone, UserCog, Users } from "lucide-react";
import { useCallback, useState } from "react";

import { Menu, MenuItem, MenuSeparator } from "../components/Menu";
import { Badge } from "../components/ui";
import { t } from "../i18n/i18n";
import { useAuth, useMountedAccount } from "./auth";
import { initials } from "./authState";

/** Menu du compte (docs/17 §8.3) : au pied de la barre latérale, au-dessus de la licence, et puce dans l'en-tête de
 *  l'accueil, réduite aux initiales sous 1280 px. Absent en mode hérité (aucun compte monté). */

function AccountHeader() {
  const mounted = useMountedAccount();
  const appointment = useAuth((s) => s.appointment);
  const admin = useAuth((s) => s.admin);
  if (!mounted) return null;
  return (
    <div className="px-2 pt-1.5 pb-2">
      <div className="text-ui leading-snug font-semibold break-words text-ink">
        {mounted.display_name}
        {appointment ? <span className="font-normal text-ink-muted"> · {appointment}</span> : null}
      </div>
      <div className="mt-0.5 text-meta leading-snug text-ink-muted">{mounted.login}</div>
      {admin && (
        <Badge tone="accent" className="mt-1.5">
          {t("Administrateur de l'unité")}
        </Badge>
      )}
    </div>
  );
}

function AccountItems() {
  const openDialog = useAuth((s) => s.openDialog);
  const actions = useAuth((s) => s.actions);
  const admin = useAuth((s) => s.admin);
  return (
    <>
      <AccountHeader />
      <MenuSeparator />
      <MenuItem icon={KeyRound} label={t("Changer de mot de passe")} onSelect={() => openDialog("password")} />
      <MenuItem icon={MonitorSmartphone} label={t("Mes sessions")} onSelect={() => openDialog("sessions")} />
      <MenuItem icon={History} label={t("Mes événements")} onSelect={() => openDialog("events")} />
      {admin && <MenuItem icon={UserCog} label={t("Comptes de l'unité")} onSelect={() => openDialog("accounts")} />}
      <MenuSeparator />
      <MenuItem icon={Lock} label={t("Verrouiller la session")} onSelect={actions.lock} />
      <MenuItem icon={Users} label={t("Changer d'utilisateur")} onSelect={actions.switchUser} />
      <MenuItem icon={LogOut} label={t("Se déconnecter")} onSelect={actions.signOut} />
    </>
  );
}

/** Pastille ronde aux initiales de la personne. */
function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={clsx("inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-meta font-semibold text-accent", className)}
    >
      {initials(name)}
    </span>
  );
}

/** Ligne du compte au pied de la barre latérale (dépliée : nom et fonction ; rail : initiales). */
export function AccountButton({ collapsed }: { collapsed: boolean }) {
  const mounted = useMountedAccount();
  const appointment = useAuth((s) => s.appointment);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  if (!mounted) return null;
  const label = t("Compte de {nom}", { nom: mounted.display_name });
  return (
    <div className={clsx("shrink-0 border-t border-line", collapsed ? "flex justify-center py-1.5" : "px-1.5 py-1.5")}>
      <Menu
        open={open}
        onClose={close}
        portal
        side={collapsed ? "right" : "top"}
        align={collapsed ? "end" : "start"}
        width={264}
        ariaLabel={label}
        wrapperClassName={collapsed ? undefined : "flex w-full"}
        trigger={
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            title={label}
            aria-label={label}
            className={clsx(
              "flex items-center gap-2 rounded-lg text-left transition-colors hover:bg-hover",
              collapsed ? "size-8 justify-center" : "min-h-9 w-full px-1.5 py-1",
            )}
          >
            <Avatar name={mounted.display_name} />
            {!collapsed && (
              <span className="min-w-0 flex-1">
                <span className="block truncate text-ui leading-snug font-medium text-ink">{mounted.display_name}</span>
                {appointment && <span className="block truncate text-meta leading-snug text-ink-muted">{appointment}</span>}
              </span>
            )}
          </button>
        }
      >
        <AccountItems />
      </Menu>
    </div>
  );
}

/** Puce du compte dans l'en-tête de l'accueil : nom, réduit aux initiales sous 1280 px. */
export function AccountChip() {
  const mounted = useMountedAccount();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  if (!mounted) return null;
  const label = t("Compte de {nom}", { nom: mounted.display_name });
  return (
    <Menu
      open={open}
      onClose={close}
      align="end"
      width={264}
      ariaLabel={label}
      trigger={
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          title={label}
          aria-label={label}
          className="inline-flex h-8 shrink-0 items-center gap-2 rounded-lg px-1.5 text-ui font-medium whitespace-nowrap text-ink transition-colors hover:bg-hover"
        >
          <Avatar name={mounted.display_name} />
          <span className="max-xl:hidden">{mounted.display_name}</span>
        </button>
      }
    >
      <AccountItems />
    </Menu>
  );
}
